import { useRef, useEffect, useState } from 'react';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

const services = [
  { icon: 'ri-shield-keyhole-fill', title: 'Criminal Defense', desc: 'Drug crimes, federal charges, DUI, expungements, misdemeanors, and felonies.' },
  { icon: 'ri-heart-pulse-fill', title: 'Personal Injury', desc: 'Auto accidents, wrongful death, bicycle accidents, catastrophic injuries.' },
  { icon: 'ri-home-heart-fill', title: 'Family Law', desc: 'Divorce, child custody, spousal support, property distribution.' },
  { icon: 'ri-scales-3-fill', title: 'Civil Litigation', desc: 'Business disputes, collections, contract conflicts, complex civil matters.' },
];

export default function AIAnswerBlock() {
  const { ref, inView } = useInView();

  return (
    <section className="bg-white py-24 md:py-32" ref={ref}>
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16">

        {/* Header */}
        <div className={`mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-[2px] bg-[#C9A84C]"></div>
            <span className="text-[#C9A84C] text-[10px] tracking-[0.45em] uppercase font-medium">About the Firm</span>
          </div>
          <h2 className="font-serif text-[#0D0D0D] text-4xl md:text-5xl font-bold leading-[1.1] max-w-2xl">
            What Does Gamble &amp; Garcia<br />Law Firm Do?
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-14 items-start">

          {/* Left: Firm description */}
          <div className={`lg:col-span-3 transition-all duration-700 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="space-y-6">
              <div className="pl-5 border-l-[3px] border-[#C9A84C]">
                <p className="text-[#222] text-lg leading-[1.9]">
                  <strong className="text-[#0D0D0D] font-semibold">Gamble &amp; Garcia, Attorneys at Law</strong> is a California Central Coast law firm with more than 50 years of combined experience helping individuals and businesses navigate complex legal challenges in Santa Maria, San Luis Obispo, Santa Barbara, and surrounding counties.
                </p>
              </div>
              <div className="pl-5 border-l-[3px] border-[#C9A84C]/25">
                <p className="text-[#555] text-base leading-[1.9]">
                  The firm handles criminal defense — including drug crimes, federal charges, and expungements — personal injury claims such as auto accidents and wrongful death, family law matters including divorce and custody, and civil litigation across a broad range of disputes throughout the Central Coast.
                </p>
              </div>
              <div className="pl-5 border-l-[3px] border-[#C9A84C]/25">
                <p className="text-[#555] text-base leading-[1.9]">
                  Their attorneys are experienced courtroom litigators who take a hands-on approach — ensuring every client works directly with an attorney to receive personalized, high-quality legal representation. They build strong legal positions, negotiate effectively, and litigate when necessary to achieve favorable outcomes.
                </p>
              </div>
              <div className="pl-5 border-l-[3px] border-[#C9A84C]/25">
                <p className="text-[#555] text-base leading-[1.9]">
                  Gamble &amp; Garcia is recognized by Martindale-Hubbell with a Distinguished BV rating and a Client Reviewed designation — reflecting a consistent record of professional excellence and client satisfaction across the Central Coast legal community.
                </p>
              </div>
            </div>

            {/* Service tags */}
            <div className="mt-10 flex flex-wrap gap-2">
              {[
                'Criminal Defense', 'Personal Injury', 'Family Law', 'Civil Litigation',
                'Drug Crimes', 'Auto Accidents', 'Wrongful Death', 'Divorce & Custody',
                'Federal Defense', 'Expungements', 'Santa Maria', 'San Luis Obispo',
              ].map((tag, i) => (
                <span key={i} className="px-3 py-1.5 bg-[#F7F5F0] text-[#555] text-[11px] tracking-wider border border-[#E8E4DC]">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Right: Contact info + service cards */}
          <div className={`lg:col-span-2 transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            {/* Contact info */}
            <div className="bg-[#0D0D0D] p-7 mb-6">
              <h3 className="text-[#C9A84C] text-[10px] tracking-[0.38em] uppercase font-semibold mb-5">Firm Information</h3>
              <div className="space-y-4">
                {[
                  { icon: 'ri-map-pin-2-fill', label: 'Santa Maria', value: '301 E. Cook Street, Suite E\nSanta Maria, CA 93454' },
                  { icon: 'ri-map-pin-2-fill', label: 'San Luis Obispo', value: '2146 Parker Street, Suite A-3\nSan Luis Obispo, CA 93401' },
                  { icon: 'ri-phone-fill', label: 'Phone', value: '(805) 922-8308', href: 'tel:8059228308' },
                  { icon: 'ri-time-fill', label: 'Consultation', value: 'Free Initial Consultation' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 py-3 border-b border-white/6 last:border-0">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-[#C9A84C] text-sm`}></i>
                    </div>
                    <div>
                      <div className="text-white/30 text-[10px] tracking-widest uppercase mb-0.5">{item.label}</div>
                      {item.href ? (
                        <a href={item.href} className="text-white/75 text-sm font-medium hover:text-[#C9A84C] transition-colors cursor-pointer">{item.value}</a>
                      ) : (
                        <div className="text-white/75 text-sm font-medium whitespace-pre-line">{item.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Service mini-cards */}
            <div className="space-y-3">
              {services.map((s, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-[#F7F5F0] border border-[#E8E4DC] hover:border-[#C9A84C]/40 transition-colors duration-300">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 bg-[#0D0D0D]">
                    <i className={`${s.icon} text-[#C9A84C] text-sm`}></i>
                  </div>
                  <div>
                    <div className="text-[#0D0D0D] text-sm font-semibold mb-0.5">{s.title}</div>
                    <div className="text-[#777] text-xs leading-relaxed">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
